import { calculateAnswerScore } from "./calculateAnswerScore.js";

const hasAnswer = (value, other = "") => {
  if (Array.isArray(value)) {
    return value.length > 0 || (typeof other === "string" && other.trim() !== "");
  }
  if (typeof value === "object" && value !== null) {
    return Object.keys(value).length > 0;
  }
  if (typeof value === "string") {
    return value.trim() !== "";
  }
  return false;
};

export const buildScoreMap = choices =>
  choices.reduce((acc, obj) => {
    const [key, value] = Object.entries(obj)[0];
    acc[key] = value;
    return acc;
  }, {});

export const computeScore = (userAnswers = [], scoreMap = {}, max) => {
  const total = userAnswers.reduce((sum, answer) => sum + (scoreMap[answer] ?? 0), 0);
  return typeof max === "number" ? Math.min(total, max) : total;
};

export const formatScore = score => {
  let str = score.toFixed(2).replace(".", ",");
  if (str.endsWith(",00")) return str.slice(0, -3);
  if (str.endsWith("0")) return str.slice(0, -1);
  return str;
};

export async function computeAllScores(responses = {}) {
  let scoreTaille = 0;
  let totalInfrastructure = 0;
  let totalHumain = 0;
  let totalProcessus = 0;
  let totalStrategie = 0;
  let totalInfrastructureMax = 0;
  let totalHumainMax = 0;
  let totalProcessusMax = 0;
  let totalStrategieMax = 0;

  // --- QUESTION 8 : Taille ---
  const taille = responses?.q22_8Quel;
  if (taille === 1) scoreTaille = 0;
  else if (taille < 5) scoreTaille = 1;
  else scoreTaille = 2;

	// --- QUESTION 13 : Biens physiques ---
  const biensPhysiques = responses?.q28_13Votre;

	// --- QUESTION 7 : Secteur Type Long --- 
	const allowedSecteurText = [
    "Industrie - Industrie Lourde (extractives et métallurgiques)",
    "Industrie - Construction d'équipements et de machines",
    "Industrie - Industries alimentaires",
    "Industrie - Chimie et plastiques",
    "Industrie - Autres industries : bois, textile, cuir, papier, verre, transport d'électricité, etc.",
    "Construction - Génie civil (construction de routes, réseaux et autres ouvrages)",
    "Construction - Gros-œuvre (construction de bâtiments, maçonnerie, démolitions, etc.)",
  ];

  // --- QUESTION 14 (Humain) ---
  const q14 = responses["q30_14Dans[]"];
  if (scoreTaille === 2 && hasAnswer(q14)) {
    const choices14 = [
      { "Stratégie digitale et pilotage des projets numériques": 1 },
      { "Communication digitale et marketing": 1 },
      { "Graphisme et design Web": 1 },
      { "Analyse et gestion de données": 1 },
      { "E-commerce": 1 },
      { "Développement, programmation, intégration de logiciels, applications et systèmes": 1 },
      { "Cyber sécurité, Cloud et réseaux": 1 },
      { "Intelligence artificielle": 1 },
      { "Numérique responsable": 1 },
      { "Automatisation/robotisation": 1 },
      { "Aucune de ces responsabilités ne sont assumées en interne": 0 },
      { "Autre à préciser": 1 },
    ];
    const raw14 = calculateAnswerScore(q14, choices14);
    const addHumain = Math.min(raw14, 2);
    totalHumain += addHumain;
    totalHumainMax += 2;
  }

  // --- QUESTION 16 (Humain) ---
  const q16 = responses?.q40_16Sur;
  if (hasAnswer(q16)) {
    const addHumain = q16 >= 7 ? 1 : 0;
    totalHumain += addHumain;
    totalHumainMax += 1;
  }

  // --- QUESTION 17 (Infrastructure) ---
  const q17 = responses?.q40_16Sur;
  if (scoreTaille > 0 && hasAnswer(q17)) {
    const score17 = q17 !== "Ne sait pas" && q17 !== "Moins de 10.000€" ? 1 : 0;
    totalInfrastructure += score17;
    totalInfrastructureMax += 1;
  }

  // --- QUESTION 18 (Infrastructure) ---
  const q18 = responses["q45_18Quels[]"];
  if (hasAnswer(q18)) {
    const choices18 = [
      { "ADSL ou VDSL": 0.5 },
      { "FTTH (fibre optique)": 1 },
      { "Ligne louée par l’entreprise": 1 },
      { "4G/5G (connexion mobile)": 0.5 },
      { "Câble coaxial (TV) (Voo, Telenet, Orange)": 0.5 },
      { "Pas de connexion à Internet": 0 },
    ];
    const raw18 = calculateAnswerScore(q18, choices18);
    const addInfra = Math.min(raw18, 2);
    totalInfrastructure += addInfra;
    totalInfrastructureMax += 2;
  }

  // --- QUESTION 22 (Humain) ---
  const q22 = responses?.q45_18Quels;
  if (scoreTaille > 0 && hasAnswer(q22)) {
    const score22 =
      q22 === "Oui, tout le personnel" ? 2 :
      q22 === "Oui, certains collaborateurs" ? 1 : 0;
    totalHumain += score22;
    totalHumainMax += 2;
  }

  // --- QUESTION 23 (Humain) ---
  const q23 = responses?.q49_dansVotre49;
  if (scoreTaille > 0 && hasAnswer(q23)) {
    const score23 =
      q23 === "Certains membres du personnel bénéficient d’un GSM professionnel avec une intervention de l’employeur dans le coût d’abonnement" ? 1 :
      q23 === "Certains membres du personnel bénéficient d’une intervention de l’employeur dans le coût de leur abonnement mobile mais n’ont pas reçu de GSM professionnel" ? 0.5 :
      0;
    totalHumain += score23;
    totalHumainMax += 1;
  }

  // --- QUESTION 24 (Infrastructure + Processus) ---
  const q24 = responses?.q52_quelleEst52;
  if (hasAnswer(q24)) {
    const choices24 = [
      { "Mon entreprise ne s'intéresse pas à la collecte ni à l'utilisation des données": 0 },
      { "Mon entreprise collecte manuellement des données provenant de différentes sources": 0.5 },
      { "Mon entreprise structure ses données et les utilise pour faciliter la prise de décision dans certaines situations": 1 },
      { "Mon entreprise intègre systématiquement les données émanant des sources disponibles dans l'ensemble de ses décisions": 2 },
    ];
    const s24 = calculateAnswerScore(q24, choices24);
    const addInfra = Math.min(s24, 2);
    const addProc = Math.min(s24, 2);

    totalInfrastructure += addInfra;
    totalInfrastructureMax += 2;

    totalProcessus += addProc;
    totalProcessusMax += 2;
  }

  // --- QUESTION 26 (Humain + Processus + Stratégie) ---
  const q26 = responses?.q54_votreEntreprise54;
  if (scoreTaille > 0 && hasAnswer(q26)) {
    const choices26 = [
      { "Oui, c'est formalisé et mis en œuvre": 1 },
      { "Pas encore, mais c'est prévu dans l'année qui vient": 0.5 },
      { "Non, et ce n’est pas planifié": 0 },
      { "Ne sait pas": 0 },
    ];
    const s26 = calculateAnswerScore(q26, choices26);

    totalHumain += s26;
    totalHumainMax += 1;

    totalProcessus += s26;
    totalProcessusMax += 1;

    totalStrategie += s26;
    totalStrategieMax += 1;
  }

  // --- QUESTION 27 (Infrastructure + Processus) ---
  const q27 = responses?.["q55_quelsSont55[]"];
  if (hasAnswer(q27)) {
    // Infra
    const choices55_infra = [
      { "Assurer la conformité réglementaire et juridique": 1 },
      { "Faciliter l'accès et l'utilisation des données par les équipes et la direction": 1 },
      { "Sécuriser les données sensibles et protéger la confidentialité": 1 },
    ];
    const addInfra = Math.min(calculateAnswerScore(q27, choices55_infra), 2);
    totalInfrastructure += addInfra;
    totalInfrastructureMax += 2;

    // Processus
    const choices55_proc = [
      { "Améliorer l'efficacité des processus de gestion des données": 1 },
      { "Renforcer la transparence et la traçabilité des données": 1 },
    ];
    const addProc = Math.min(calculateAnswerScore(q27, choices55_proc), 2);
    totalProcessus += addProc;
    totalProcessusMax += 2;
  }

  // --- QUESTION 28 (Processus) ---
  const q28 = responses?.["q57_28Quels[]"];
  if (scoreTaille > 0 && hasAnswer(q28)) {
    const choices28 = [
      { "Amélioration de la prise de décision": 1 },
      { "Augmentation de la productivité et des performances": 1 },
      { "Monitoring de l'activité en temps réel": 1 },
      { "Monétisation des données de l'entreprise": 1 },
      { "Identification de nouvelles opportunités commerciales": 1 },
      { "Amélioration de l'existant": 1 },
      { "Développement de nouveaux produits/services": 1 },
      { "Amélioration de la gestion des risques": 1 },
      { "Conformité réglementaire accrue": 1 },
      { "Autre à préciser": 0.5 },
    ];
    const raw28 = calculateAnswerScore(q28, choices28);
    const addProc = Math.min(raw28, 2);
    totalProcessus += addProc;
    totalProcessusMax += 2;
  }

  // --- QUESTION 30 (Stratégie) ---
  const q30 = responses?.q61_votreEntreprise61;
  if (scoreTaille > 0 && hasAnswer(q30)) {
    const addStrat = q30 === "Oui" ? 1 : 0;
    totalStrategie += addStrat;
    totalStrategieMax += 1;
  }

  // --- QUESTION 31 (A → G, répartitions) ---
  const q31 = responses || {};

  // 31_A (Infra)
  const q31_A = q31["q62_31Parmi[0]"];
  if (hasAnswer(q31_A)) {
    const addInfra = q31_A === "Oui" ? 1 : 0;
    totalInfrastructure += addInfra;
    totalInfrastructureMax += 1;
  }

  // 31_B (Infra + Processus) — Oui = 2 points sur chaque axe (borné par max)
  const q31_B = q31["q62_31Parmi[1]"];
  if (hasAnswer(q31_B)) {
    const add = q31_B === "Oui" ? 2 : 0;
    totalInfrastructure += add;
    totalInfrastructureMax += 2;
    totalProcessus += add;
    totalProcessusMax += 2;
  }

  // 31_C (Humain + Processus + Stratégie) — condition taille > 0
  const q31_C = q31["q62_31Parmi[2]"];
  if (scoreTaille > 0 && hasAnswer(q31_C)) {
    const add = q31_C === "Oui" ? 1 : 0;
    totalHumain += add;
    totalHumainMax += 1;
    totalProcessus += add;
    totalProcessusMax += 1;
    totalStrategie += add;
    totalStrategieMax += 1;
  }

  // 31_D (Infra + Processus)
  const q31_D = q31["q62_31Parmi[3]"];
  if (hasAnswer(q31_D)) {
    const add = q31_D === "Oui" ? 1 : 0;
    totalInfrastructure += add;
    totalInfrastructureMax += 1;
    totalProcessus += add;
    totalProcessusMax += 1;
  }

  // 31_E (Infra + Processus)
  const q31_E = q31["q62_31Parmi[4]"];
  if (hasAnswer(q31_E)) {
    const add = q31_E === "Oui" ? 1 : 0;
    totalInfrastructure += add;
    totalInfrastructureMax += 1;
    totalProcessus += add;
    totalProcessusMax += 1;
  }

  // 31_F (Infra + Processus)
  const q31_F = q31["q62_31Parmi[5]"];
  if (hasAnswer(q31_F)) {
    const add = q31_F === "Oui" ? 1 : 0;
    totalInfrastructure += add;
    totalInfrastructureMax += 1;
    totalProcessus += add;
    totalProcessusMax += 1;
  }

  // 31_G (Infra + Processus)
  const q31_G = q31["q62_31Parmi[6]"];
  if (hasAnswer(q31_G)) {
    const add = q31_G === "Oui" ? 1 : 0;
    totalInfrastructure += add;
    totalInfrastructureMax += 1;
    totalProcessus += add;
    totalProcessusMax += 1;
  }

  // --- QUESTION 32 (Infrastructure) ---
  const q32 = responses?.q63_32En;
  if (scoreTaille > 0 && hasAnswer(q32)) {
    const addInfra = q32 !== "Pas d'investissement" && q32 !== "Ne veut pas répondre / Ne sait pas" ? 1 : 0;
    totalInfrastructure += addInfra;
    totalInfrastructureMax += 1;
  }

  // --- QUESTION 33 (Stratégie) ---
  const q33 = responses?.q64_votreEntreprise64;
	if (scoreTaille > 0 && hasAnswer(q33)) {
		const choices33 = [
			{ "Oui, il y a moins d'un an": 1 },
			{ "Oui, il y a plus d'un an": 0 },
			{ "Non, jamais": 0 },
			{ "Ne veut pas répondre / Ne sait pas": 0 },
		];
		const addStrat = calculateAnswerScore(q33, choices33);
		totalStrategie += addStrat;
		totalStrategieMax += 1;
	}

  // --- QUESTION 35 (Stratégie + Humain) ---
  const q35 = responses?.q71_votreEntreprise71;
  if (scoreTaille === 2 && hasAnswer(q35)) {
    const choices35 = [
      { "Oui, pour toute l'entreprise": 1 },
      { "Oui, partiellement au sein de certains départements, pour certains projets": 1 },
      { "Non": 0 },
      { "Ne sait pas": 0 },
    ];
    const add = Math.min(calculateAnswerScore(q35, choices35), 1);
    totalStrategie += add;
    totalStrategieMax += 1;
    totalHumain += add;
    totalHumainMax += 1;
  }

  // --- QUESTION 36 (Humain) ---
  const q36 = responses?.["q72_quelsSont72[]"];
  const q35_answer = responses?.q71_votreEntreprise71;
  const canScoreQ36 =
    scoreTaille === 2 &&
    (q35_answer === "Oui, pour toute l'entreprise" ||
     q35_answer === "Oui, partiellement au sein de certains départements, pour certains projets");
  if (canScoreQ36 && hasAnswer(q36)) {
    const choices36 = [
      { "Les objectifs et indicateurs": 1 },
      { "Les ressources financières et humaines": 1 },
      { "La planification et la priorisation des projets": 1 },
      { "Les modalités de gestion des risques": 1 },
      { "Les modalités de gestion du changement": 1 },
      { "Ne sait pas": 0 },
      { "Autre à préciser": 0 },
    ];
    const raw36 = calculateAnswerScore(q36, choices36);
    const addHumain = Math.min(raw36, 2);
    totalHumain += addHumain;
    totalHumainMax += 2;
  }

  // --- QUESTION 37 (Humain) ---
  const q37 = responses?.["q73_quellesSont73[]"];
  if (scoreTaille === 2 && hasAnswer(q37)) {
    const choices37 = [
      { "Le directeur": 0.5 },
      { "Le responsable informatique": 0.5 },
      { "Le responsable des ressources humaines": 1 },
      { "Le ou les responsables d'équipes": 1 },
      { "Les collaborateurs concernés par le changement digital": 1 },
      { "Autre": 0 },
      { "Ne sait pas": 0 },
    ];
    const raw37 = calculateAnswerScore(q37, choices37);
    const addHumain = Math.min(raw37, 2);
    totalHumain += addHumain;
    totalHumainMax += 2;
  }

  // --- QUESTION 38 (Humain) ---
  const q38 = responses?.["q74_quellesSont74[]"];
  if (scoreTaille === 2 && hasAnswer(q38)) {
    const choices38 = [
      { "Compétences techniques en informatique": 0.5 },
      { "Compétences en gestion de projets": 1 },
      { "Compétences liées au métier concerné par la digitalisation": 1 },
      { "Compétences en gestion du changement": 1 },
      { "Compétences en analyse de données et/ou des processus métiers": 0.5 },
      { "Autre": 0 },
      { "Ne sait pas": 0 },
    ];
    const raw38 = calculateAnswerScore(q38, choices38);
    const addHumain = Math.min(raw38, 2);
    totalHumain += addHumain;
    totalHumainMax += 2;
  }

  // --- QUESTION 40 (Humain + Processus + Stratégie) ---
  const q40 = responses?.["q77_quelsSont77[]"];
  const q40_other = responses?.["q77_quelsSont77[other]"];
  if (hasAnswer(q40, q40_other)) {
    // Humain (seulement 3 choix)
    const choices40_humain = [
      { "Veille technologique": 1 },
      { "Formation continue": 1 },
      { "Conférences et salons": 1 },
      { "Collaboration avec des centres de recherche": 0 },
      { "Collaboration avec des start ups": 0 },
      { "Analyse de données": 0 },
      { "Ne sait pas": 0 },
      { "Autre": 0 },
    ];
    const raw40_h = calculateAnswerScore(q40, choices40_humain);
    const addHumain = Math.min(raw40_h, 3);
    totalHumain += addHumain;
    totalHumainMax += 3;

    // Processus (uniquement "Analyse de données")
    const choices40_proc = [{ "Analyse de données": 1 }];
    const raw40_p = calculateAnswerScore(q40, choices40_proc);
    const addProc = Math.min(raw40_p, 1);
    totalProcessus += addProc;
    totalProcessusMax += 1;
    // Stratégie (collaborations externes)
    const choices40_strat = [
      { "Collaboration avec des centres de recherche": 1 },
      { "Collaboration avec des start ups": 1 },
    ];
    const raw40_s = calculateAnswerScore(q40, choices40_strat);
    const addStrat = Math.min(raw40_s, 2);
    totalStrategie += addStrat;
    totalStrategieMax += 2;
  }

  // --- QUESTION 41 (Humain) ---
  const q41 = responses?.q78_auCours78;
  if (scoreTaille === 2 && hasAnswer(q41)) {
    const addHumain = Number(q41) >= 2 ? 1 : 0;
    totalHumain += addHumain;
    totalHumainMax += 1;
  }

  // --- QUESTION 42 (Humain) ---
  const q42 = responses?.q79_42Au;
  if (scoreTaille > 0 && hasAnswer(q42)) {
    const addHumain = q42 === "Oui" ? 1 : 0;
    totalHumain += addHumain;
    totalHumainMax += 1;
  }

  // --- QUESTION 43 (Humain) ---
  const q43 = responses?.["q80_quellesSont80[]"];
  const q43_other = responses?.["q80_quellesSont80[other]"];
  if (scoreTaille > 0 && q42 === "Oui" && hasAnswer(q43, q43_other)) {
    const choices43 = [
      { "Formations dispensées par des organismes externes": 1 },
      { "Ateliers entre collaborateurs, mentorat, coaching, etc.": 1 },
      { "Mise à disposition de ressources en ligne : MOOC, e-learning, webinaire, etc.": 0.5 },
      { "Incitants à l'autoapprentissage (proposition de formations régulières, budget spécifique pour la formation, etc.)": 1 },
      { "Ne sait pas": 0 },
      { "Autre à préciser": 1 },
    ];
    const raw43 = calculateAnswerScore(q43, choices43);
    const addHumain = Math.min(raw43, 2);
    totalHumain += addHumain;
    totalHumainMax += 2;
  }

  // --- QUESTION 44 (Infrastructure + Humain) ---
  const q44 = responses?.q83_votreEntreprise83;
  if (hasAnswer(q44)) {
    const add = q44 === "Oui" ? 1 : 0;
    totalInfrastructure += add;
    totalInfrastructureMax += 1;
    totalHumain += add;
    totalHumainMax += 1;
  }

  // --- QUESTION 45 (Infrastructure + Processus) ---
  const q45 = responses?.q84_votreEntreprise84;
  if (scoreTaille > 0 && hasAnswer(q45)) {
    const add = q45 === "Oui" ? 1 : 0;
    totalInfrastructure += add;
    totalInfrastructureMax += 1;
    totalProcessus += add;
    totalProcessusMax += 1;
  }

  // --- QUESTION 46 (Humain) ---
  const q46 = responses?.q85_votreEntreprise85;
  if (scoreTaille === 2 && hasAnswer(q46)) {
    const scoreMap46 = {
      "Oui, tous les collaborateurs": 1,
      "Oui, certains collaborateurs": 1,
      "En projet": 0,
      "Non": 0,
      "Ne s'applique pas (entreprise unipersonnelle, par exemple)": 0,
    };
    const addHumain = scoreMap46[q46] ?? 0;
    totalHumain += addHumain;
    totalHumainMax += 1;
  }

  // --- QUESTION 47 (Humain) ---
  const q47 = responses?.["q266_votreEntreprise266[]"];
  const q47_other = responses?.["q266_votreEntreprise266[other]"];
  const canAnswer47 =
    scoreTaille > 0 &&
    (q46 === "Oui, tous les collaborateurs" || q46 === "Oui, certains collaborateurs");
  if (canAnswer47 && hasAnswer(q47, q47_other)) {
    const choices47 = [
      { "Oui, via une gestion du travail orientée résultats": 1 },
      { "Oui, via une organisation du travail par projets": 1 },
      { "Oui, via une plus grande flexibilité du temps de travail": 1 },
      { "Oui, via des processus de décision plus participatifs": 1 },
      { "Non, notre dynamique managériale n'a pas changé": 0 },
      { "Ne sait pas": 0 },
      { "Autre": 0 },
    ];
    const raw47 = calculateAnswerScore(q47, choices47);
    const addHumain = Math.min(raw47, 2);
    totalHumain += addHumain;
    totalHumainMax += 2;
  }

  // --- QUESTION 48 (Humain) ---
  const q48 = responses?.q88_dansVotre88;
  if (scoreTaille > 0 && hasAnswer(q48)) {
    const choices48 = [
      { "Oui": 1 },
      { "Non": 0 },
      { "Ne sait pas": 0 },
    ];
    const addHumain = calculateAnswerScore(q48, choices48);
    totalHumain += addHumain;
    totalHumainMax += 1;
  }

	  // --- QUESTION 49 (Humain) ---
  const q49 = responses?.["q89_quellesSont89[]"];
  const q49_other = responses?.["q89_quellesSont89[other]"];
  if (scoreTaille > 0 && responses?.q88_dansVotre88 === "Oui" && hasAnswer(q49, q49_other)) {
    const choices49 = [
      { "Sensibilisation du personnel aux risques psychosociaux liés au manque de déconnexion": 0.5 },
      { "Information du personnel sur la gestion des notifications professionnelles (sms, e-mails, Teams alerts, etc.)": 0.5 },
      { "Demande formelle de ne pas envoyer des e-mails en dehors des heures de travail": 1 },
      { "Coupure des accès aux serveurs de l'entreprise dans certains créneaux horaires": 1 },
      { "Autre à préciser": 0.5 },
    ];
    const raw49 = calculateAnswerScore(q49, choices49);
    const addHumain = Math.min(raw49, 2);
    totalHumain += addHumain;
    totalHumainMax += 2;
  }

  // --- QUESTION 50 (Stratégie) ---
  const q50 = responses?.q92_votreEntreprise92;
  if (scoreTaille > 0 && hasAnswer(q50)) {
    const choices50 = [
      { "Oui": 1 },
      { "En projet": 0 },
      { "Non": 0 },
      { "Ne sait pas": 0 },
    ];
    const addStrat = calculateAnswerScore(q50, choices50);
    totalStrategie += addStrat;
    totalStrategieMax += 1;
  }

	  // --- QUESTION 51 (Stratégie) ---
  const q51 = responses?.["q93_quellesSont93[]"];
  const q51_other = responses?.["q93_quellesSont93[other]"];
  const q50_answer = responses?.q92_votreEntreprise92;
  if (scoreTaille > 0 && q50_answer === "Oui" && hasAnswer(q51, q51_other)) {
    const choices51 = [
      { "Éco-conception des services numériques (optimisation du code pour minimiser la consommation d'énergie et de ressources, éviter l'obsolescence, ...)": 1 },
      { "Utilisation plus durable des équipements (utilisation au-delà de l'amortissement comptable, réparation, ...)": 1 },
      { "Gestion durable de la fin de vie des équipements (don, revente, recyclage, ...)": 1 },
      { "Optimisation des centres de données (serveurs locaux, écoresponsables, coupure nocturne, ...)": 1 },
      { "Sensibilisation et formation des collaborateurs à une utilisation responsable des technologies numériques": 1 },
      { "Adoption de standards et de labels environnementaux, au niveau de l'entreprise ou de ses achats (ISO 14001, ISO2600, TCO, EPEAT, Energy star, ...)": 1 },
      { "Mesure et analyse des impacts environnementaux et sociaux des technologies numériques sur l'ensemble de leur cycle de vie": 1 },
      { "Autre à préciser": 1 },
    ];
    const raw51 = calculateAnswerScore(q51, choices51);
    const addStrat = Math.min(raw51, 2);
    totalStrategie += addStrat;
    totalStrategieMax += 2;
  }

  // --- QUESTION 52 (Humain + Stratégie) ---
  const q52 = responses?.q270_52Votre;
  if (scoreTaille > 0 && hasAnswer(q52)) {
    const choices52 = [
      { "Oui": 1 },
      { "En projet": 0.5 },
      { "Non": 0 },
      { "Ne sait pas": 0 },
    ];
    const raw52 = calculateAnswerScore(q52, choices52);
    const add = Math.min(raw52, 1);
    totalHumain += add;
    totalHumainMax += 1;
    totalStrategie += add;
    totalStrategieMax += 1;
  }

	  // --- QUESTION 53 (Stratégie) ---
  const q53 = responses?.q271_53Dans;
  if (scoreTaille > 0 && hasAnswer(q53)) {
    const choices53 = [
      { "Le numérique aidera beaucoup": 1 },
      { "Le numérique aidera": 0.8 },
      { "Le numérique aidera un peu": 0.5 },
      { "Le numérique n'aidera pas l'entreprise à fonctionner de façon circulaire": 0 },
      { "Ne sait pas": 0 },
    ];
    const raw53 = calculateAnswerScore(q53, choices53);
    const addStrat = Math.min(raw53, 1);
    totalStrategie += addStrat;
    totalStrategieMax += 1;
  }

	  // --- QUESTION 54 (Matrix : Infrastructure + Processus + Stratégie + Humain) ---
  if (scoreTaille > 0) {
    const q54 = responses || {};
    // Helper pour simplifier la lecture du code
    const scoreOui = val => {
      if (typeof val !== "string") return 0;
      const cleaned = val.replace(/\s+/g, "").toLowerCase();
      return cleaned === "oui" ? 1 : 0;
    };
    // A — CRM : Infra + Processus + Stratégie
    const q54_A = q54["q96_votreEntreprise96[0]"];
    if (hasAnswer(q54_A)) {
      const add = scoreOui(q54_A);
      totalInfrastructure += add;
      totalInfrastructureMax += 1;
      totalProcessus += add;
      totalProcessusMax += 1;
      totalStrategie += add;
      totalStrategieMax += 1;
    }

    // B — Réseaux sociaux externes : Stratégie
    const q54_B = q54["q96_votreEntreprise96[1]"];
    if (hasAnswer(q54_B)) {
      const add = scoreOui(q54_B);
      totalStrategie += add;
      totalStrategieMax += 1;
    }

    // C — GED : Infra + Processus
    const q54_C = q54["q96_votreEntreprise96[2]"];
    if (hasAnswer(q54_C)) {
      const add = scoreOui(q54_C);
      totalInfrastructure += add;
      totalInfrastructureMax += 1;
      totalProcessus += add;
      totalProcessusMax += 1;
    }

    // D — Gestion des données : Infra + Processus
    const q54_D = q54["q96_votreEntreprise96[3]"];
    if (hasAnswer(q54_D)) {
      const add = scoreOui(q54_D);
      totalInfrastructure += add;
      totalInfrastructureMax += 1;
      totalProcessus += add;
      totalProcessusMax += 1;
    }

    // E — Gestion de site web : Infra + Stratégie
    const q54_E = q54["q96_votreEntreprise96[4]"];
    if (hasAnswer(q54_E)) {
      const add = scoreOui(q54_E);
      totalInfrastructure += add;
      totalInfrastructureMax += 1;
      totalStrategie += add;
      totalStrategieMax += 1;
    }

    // F — Tableaux de bord : Humain + Processus
    const q54_F = q54["q96_votreEntreprise96[5]"];
    if (hasAnswer(q54_F)) {
      const add = scoreOui(q54_F);
      totalHumain += add;
      totalHumainMax += 1;
      totalProcessus += add;
      totalProcessusMax += 1;
    }

    // G — Gestion des prestations : Infra + Humain + Processus
    const q54_G = q54["q96_votreEntreprise96[6]"];
    if (hasAnswer(q54_G)) {
      const add = scoreOui(q54_G);
      totalInfrastructure += add;
      totalInfrastructureMax += 1;
      totalHumain += add;
      totalHumainMax += 1;
      totalProcessus += add;
      totalProcessusMax += 1;
    }

    // H — Gestion des carrières : Infra + Humain + Processus
    const q54_H = q54["q96_votreEntreprise96[7]"];
    if (hasAnswer(q54_H)) {
      const add = scoreOui(q54_H);
      totalInfrastructure += add;
      totalInfrastructureMax += 1;
      totalHumain += add;
      totalHumainMax += 1;
      totalProcessus += add;
      totalProcessusMax += 1;
    }

    // I — ERP : Infra + Processus
    const q54_I = q54["q96_votreEntreprise96[8]"];
    if (hasAnswer(q54_I)) {
      const add = scoreOui(q54_I);
      totalInfrastructure += add;
      totalInfrastructureMax += 1;
      totalProcessus += add;
      totalProcessusMax += 1;
    }
  }

  // --- QUESTION 55A et 55B (Infrastructure + Processus) ---
  const q55A = responses?.["q97_55Au[0]"];
  const q55B = responses?.["q97_55Au[1]"];
  const canAnswer55 = scoreTaille === 2 && biensPhysiques === "Oui";
  if (canAnswer55 && hasAnswer(q55A)) {
    const add = q55A === "Oui" ? 1 : 0;
    totalInfrastructure += add;
    totalInfrastructureMax += 1;
    totalProcessus += add;
    totalProcessusMax += 1;
  }
  if (canAnswer55 && hasAnswer(q55B)) {
    const add = q55B === "Oui" ? 1 : 0;
    totalInfrastructure += add;
    totalInfrastructureMax += 1;
    totalProcessus += add;
    totalProcessusMax += 1;
  }

	// --- QUESTION 58 (Infrastructure + Processus) ---
  const q58 = responses?.["q101_58Pour[]"];
  const q58_other = responses?.["q101_58Pour[other]"];
  const q100 = responses?.q100_quelleEst100;
  const canAnswer58 = q100 === "Cloud" || q100 === "Hybride (mixte)";
  if (canAnswer58 && hasAnswer(q58, q58_other)) {
    const choices58 = [
      { "Paiement à l'utilisation (scalability)": 1 },
      { "Flexibilité du stockage": 1 },
      { "Sécurité": 1 },
      { "Robustesse du service": 1 },
      { "Intégration avec d'autres services de l'écosystème comme Teams, SharePoint, Office 365, etc.": 1 },
      { "Collaboration améliorée sur des ressources partagées": 1 },
      { "Utilisation de solutions de type SaaS (Software as a service)": 1 },
      { "Autre": 0.5 },
    ];
    const raw58 = calculateAnswerScore(q58, choices58);
    const addInfra = Math.min(raw58, 2);
    const addProc = Math.min(raw58, 2);
    totalInfrastructure += addInfra;
    totalInfrastructureMax += 2;
    totalProcessus += addProc;
    totalProcessusMax += 2;
  }

	  // --- QUESTION 59 (Matrix : Infrastructure + Processus) ---
  if (scoreTaille > 0) {
    const q59 = responses || {};
    const scoreOui = (val) => {
      if (typeof val !== "string") return 0;
      const cleaned = val.replace(/\s+/g, "").toLowerCase();
      return cleaned === "oui" ? 1 : 0;
    };
    // A — Blockchain
    const q59_A = q59["q292_59Parmi[0]"];
    if (hasAnswer(q59_A)) {
      const add = scoreOui(q59_A);
      totalInfrastructure += add;
      totalInfrastructureMax += 1;
      totalProcessus += add;
      totalProcessusMax += 1;
    }

    // B — Impression 3D
    const q59_B = q59["q292_59Parmi[1]"];
    if (hasAnswer(q59_B)) {
      const add = scoreOui(q59_B);
      totalInfrastructure += add;
      totalInfrastructureMax += 1;
      totalProcessus += add;
      totalProcessusMax += 1;
    }

    // C — Analyse massive de données
    const q59_C = q59["q292_59Parmi[2]"];
    if (hasAnswer(q59_C)) {
      const add = scoreOui(q59_C);
      totalInfrastructure += add;
      totalInfrastructureMax += 1;
      totalProcessus += add;
      totalProcessusMax += 1;
    }

    // D — Calcul haute performance (HPC)
    const q59_D = q59["q292_59Parmi[3]"];
    if (hasAnswer(q59_D)) {
      const add = scoreOui(q59_D);
      totalInfrastructure += add;
      totalInfrastructureMax += 1;
      totalProcessus += add;
      totalProcessusMax += 1;
    }

    // E — Capteurs intelligents / IoT
    const q59_E = q59["q292_59Parmi[4]"];
    if (hasAnswer(q59_E)) {
      const add = scoreOui(q59_E);
      totalInfrastructure += add;
      totalInfrastructureMax += 1;
      totalProcessus += add;
      totalProcessusMax += 1;
    }

    // F — Réalité augmentée / virtuelle
    const q59_F = q59["q292_59Parmi[5]"];
    if (hasAnswer(q59_F)) {
      const add = scoreOui(q59_F);
      totalInfrastructure += add;
      totalInfrastructureMax += 1;
      totalProcessus += add;
      totalProcessusMax += 1;
    }

    // G — Robots de service
    const q59_G = q59["q292_59Parmi[6]"];
    if (hasAnswer(q59_G)) {
      const add = scoreOui(q59_G);
      totalInfrastructure += add;
      totalInfrastructureMax += 1;
      totalProcessus += add;
      totalProcessusMax += 1;
    }

    // H — Drones
    const q59_H = q59["q292_59Parmi[7]"];
    if (hasAnswer(q59_H)) {
      const add = scoreOui(q59_H);
      totalInfrastructure += add;
      totalInfrastructureMax += 1;
      totalProcessus += add;
      totalProcessusMax += 1;
    }
  }

	// --- QUESTION 60 (Matrix : Infrastructure + Processus, condition taille + secteur) ---
  const secteur = responses?.q32_7Dans;
  const canAnswer60 = scoreTaille === 2 && allowedSecteurText.includes(secteur);
  if (canAnswer60) {
    const q60 = responses || {};
    const scoreOui = val => (val?.trim() === "Oui" ? 1 : 0);
    // A — Jumeaux numériques
    const q60_A = q60["q182_parmiLes182[0]"];
    if (hasAnswer(q60_A)) {
      const add = scoreOui(q60_A);
      totalInfrastructure += add;
      totalInfrastructureMax += 1;
      totalProcessus += add;
      totalProcessusMax += 1;
    }

    // B — Systèmes embarqués
    const q60_B = q60["q182_parmiLes182[1]"];
    if (hasAnswer(q60_B)) {
      const add = scoreOui(q60_B);
      totalInfrastructure += add;
      totalInfrastructureMax += 1;
      totalProcessus += add;
      totalProcessusMax += 1;
    }

    // C — Robots de production
    const q60_C = q60["q182_parmiLes182[2]"];
    if (hasAnswer(q60_C)) {
      const add = scoreOui(q60_C);
      totalInfrastructure += add;
      totalInfrastructureMax += 1;
      totalProcessus += add;
      totalProcessusMax += 1;
    }

    // D — Systèmes de planification avancés
    const q60_D = q60["q182_parmiLes182[3]"];
    if (hasAnswer(q60_D)) {
      const add = scoreOui(q60_D);
      totalInfrastructure += add;
      totalInfrastructureMax += 1;
      totalProcessus += add;
      totalProcessusMax += 1;
    }

    // E — Postes de travail interopérables
    const q60_E = q60["q182_parmiLes182[4]"];
    if (hasAnswer(q60_E)) {
      const add = scoreOui(q60_E);
      totalInfrastructure += add;
      totalInfrastructureMax += 1;
      totalProcessus += add;
      totalProcessusMax += 1;
    }

    // F — Maintenance prédictive
    const q60_F = q60["q182_parmiLes182[5]"];
    if (hasAnswer(q60_F)) {
      const add = scoreOui(q60_F);
      totalInfrastructure += add;
      totalInfrastructureMax += 1;
      totalProcessus += add;
      totalProcessusMax += 1;
    }
  }

	// --- QUESTION 61 (Checkbox : Infrastructure + Processus + Stratégie) ---
  const q61 = responses?.["q108_quelsSont108[]"];
  if (hasAnswer(q61)) {
    const choices61 = [
      { "Utilisation d'outils prêts à l'emploi tels que ChatGPT, Microsoft CoPilot, …": 1 },
      { "Utilisation de l'IA dans le cadre de projets / produits développés sur mesure": 1 },
      { "Nous sommes une entreprise axée sur l'IA (développement et utilisation intensive de technologies d'IA)": 1 },
      { "Aucun usage de l'IA": 0 },
    ];
    const raw61 = calculateAnswerScore(q61, choices61);
    const add = Math.min(raw61, 2);
    totalInfrastructure += add;
    totalInfrastructureMax += 2;
    totalProcessus += add;
    totalProcessusMax += 2;
    totalStrategie += add;
    totalStrategieMax += 2;
  }

	// --- QUESTION 62 (Checkbox : Infrastructure + Processus, dépend de Q61) ---
  const q61_for62 = responses?.["q108_quelsSont108[]"];
  const q62 = responses?.["q110_62Dans[]"];
  const q62_other = responses?.["q110_62Dans[other]"];

  if (hasAnswer(q61_for62)) {
    const choices61_forEligibility = [
      { "Utilisation d'outils prêts à l'emploi tels que ChatGPT, Microsoft CoPilot, …": 1 },
      { "Utilisation de l'IA dans le cadre de projets / produits développés sur mesure": 1 },
      { "Nous sommes une entreprise axée sur l'IA (développement et utilisation intensive de technologies d'IA)": 1 },
      { "Aucun usage de l'IA": 0 },
    ];
    const raw61 = calculateAnswerScore(q61_for62, choices61_forEligibility);
    const canAnswer62 = raw61 > 0;
    if (canAnswer62 && hasAnswer(q62, q62_other)) {
      const choices62 = [
        { "Service RH": 1 },
        { "Production / Opérations": 1 },
        { "Service IT": 1 },
        { "Service comptable et financier": 1 },
        { "Service ventes, marketing et communication": 1 },
        { "Autre à préciser": 0.5 },
      ];
      const raw62 = calculateAnswerScore(q62, choices62);
      const add = Math.min(raw62, 2);
      totalInfrastructure += add;
      totalInfrastructureMax += 2;
      totalProcessus += add;
      totalProcessusMax += 2;
    }
  }

	// --- QUESTION 65 (Radio : Humain + Stratégie, dépend de Q61) ---
	const q61_for65 = responses?.["q108_quelsSont108[]"];
	const q65 = responses?.q113_65Comment;
	if (scoreTaille > 0 && Array.isArray(q61_for65)) {
		const eligibilityChoices = [
			{ "Utilisation d'outils prêts à l'emploi tels que ChatGPT, Microsoft CoPilot, …": 1 },
			{ "Utilisation de l'IA dans le cadre de projets / produits développés sur mesure": 0 },
			{ "Nous sommes une entreprise axée sur l'IA (développement et utilisation intensive de technologies d'IA)": 0 },
			{ "Aucun usage de l'IA": 0 },
		];
		const rawEligibility = calculateAnswerScore(q61_for65, eligibilityChoices);
		const canAnswer65 = rawEligibility > 0;
		if (canAnswer65 && hasAnswer(q65)) {
			const choices65 = [
				{ "L'utilisation de l'IA est laissée à la discrétion de chacun en fonction de ses besoins et préférences": 0 },
				{ "L'utilisation de l'IA est interdite et chaque employé respecte cette décision": 0.5 },
				{ "L'utilisation de l'IA est interdite mais je pense que certains l'utilisent sans autorisation": 0 },
				{ "L'utilisation de l'IA est autorisée uniquement à travers des outils spécifiquement identifiés et validés": 1 },
				{ "Il n'y a pas encore de cadre défini": 0 },
				{ "Ne s'applique pas (entreprise unipersonnelle, par exemple)": 0 },
			];
			const raw65 = calculateAnswerScore(q65, choices65);
			const add = Math.min(raw65, 1);
			totalHumain += add;
			totalHumainMax += 1;
			totalStrategie += add;
			totalStrategieMax += 1;
		}
	}

	// --- QUESTION 66 (Checkbox : Processus) ---
	const q66 = responses?.["q116_votreEntreprise116[]"];
	const q66_other = responses?.["q116_votreEntreprise116[other]"];
	if (scoreTaille > 0 && hasAnswer(q66, q66_other)) {
		const choices66 = [
			{ "Répondre à des appels d'offres publics": 1 },
			{ "Demander des formulaires/documents": 1 },
			{ "Faire des déclarations obligatoires (DIMONA, TVA, ISOC, etc.)": 1 },
			{ "Ne sait pas": 0 },
			{ "Autre": 0.5 },
		];
		const raw66 = calculateAnswerScore(q66, choices66);
		const addProc = Math.min(raw66, 2);
		totalProcessus += addProc;
		totalProcessusMax += 2;
	}

	// --- QUESTION 67 (Infrastructure + Processus) ---
	const q67 = responses?.q119_votreEntreprise119;
	if (hasAnswer(q67)) {
		const choices67 = [
			{ "Oui": 1 },
			{ "Non": 0 },
			{ "Ne sait pas": 0 },
		];
		const add = calculateAnswerScore(q67, choices67);
		totalInfrastructure += add;
		totalInfrastructureMax += 1;
		totalProcessus += add;
		totalProcessusMax += 1;
	}

	// --- QUESTION 68 (Infrastructure + Processus, dépend de Q67) ---
	const q67_answer = responses?.q119_votreEntreprise119;
	const q68 = responses?.q120_68Si;
	if (q67_answer === "Oui" && hasAnswer(q68)) {
		const choices68 = [
			{ "A l'aide d'un logiciel bureautique non dédié à la facturation (Word, Excel par exemple)": 0 },
			{ "A l'aide d'un programme comptable (SageBob50, WinBooks, Exact Online, Odoo, etc.)": 1 },
			{ "Sur un portail web dédié (fournisseurs, clients, (experts)-comptables, etc.)": 2 },
			{ "Ne sait pas": 0 },
		];
		const add = calculateAnswerScore(q68, choices68);
		totalInfrastructure += Math.min(add, 2);
		totalInfrastructureMax += 2;
		totalProcessus += Math.min(add, 2);
		totalProcessusMax += 2;
	}

	// --- QUESTION 69 (Infrastructure + Processus) ---
	const q69 = responses?.q121_69Sous;
	if (hasAnswer(q69)) {
		const choices69 = [
			{ "Factures PAPIER envoyées par la poste ou remises en mains propres": 0 },
			{ "Factures en fichier PDF, Word, Excel, etc. envoyées par e-mail": 1 },
			{ "Facture au format structuré (XML)": 2 },
			{ "Ne sait pas": 0 },
		];
		const add = calculateAnswerScore(q69, choices69);
		totalInfrastructure += Math.min(add, 2);
		totalInfrastructureMax += 2;
		totalProcessus += Math.min(add, 2);
		totalProcessusMax += 2;
	}

	// --- QUESTION 73 (Infrastructure + Processus) ---
	const q73 = responses?.q125_lorsqueVotre125;
	const canAnswer73 = responses?.q121_69Sous === "Facture au format structuré (XML)";
	if (canAnswer73 && hasAnswer(q73)) {
		const choices73 = [
			{ "Conversion dans un format lisible tel que Word ou Pdf pour un traitement et un archivage manuel papier": 0 },
			{ "Conversion dans un format lisible tel que Word ou Pdf pour un traitement et un archivage électronique": 0.5 },
			{ "Transfert vers une plateforme dédiée pour un traitement et un archivage (semi-)automatisé": 1 },
			{ "Ne sait pas": 0 },
		];
		const add = Math.min(calculateAnswerScore(q73, choices73), 1);
		totalInfrastructure += add;
		totalInfrastructureMax += 1;
		totalProcessus += add;
		totalProcessusMax += 1;
	}

	// --- QUESTION 74 (Stratégie) ---
	const q74 = responses?.q129_votreEntreprise129;
	if (hasAnswer(q74)) {
		const choices74 = [
			{ "Oui": 1 },
			{ "Non": 0 },
			{ "Ne sait pas": 0 },
		];
		const addStrat = Math.min(calculateAnswerScore(q74, choices74), 1);
		totalStrategie += addStrat;
		totalStrategieMax += 1;
	}

	// --- QUESTION 75 (Stratégie) ---
	const q75 = responses?.q130_votreEntreprise130;
	if (hasAnswer(q75)) {
		const choices75 = [
			{ "Oui": 1 },
			{ "Non": 0 },
			{ "Ne sait pas": 0 },
		];
		const addStrat = Math.min(calculateAnswerScore(q75, choices75), 1);
		totalStrategie += addStrat;
		totalStrategieMax += 1;
	}

	// --- QUESTION 76 (Infrastructure + Stratégie) ---
	const q76 = responses?.q131_votreEntreprise131;
	if (hasAnswer(q76)) {
		const choices76 = [
			{ "Oui": 1 },
			{ "Non": 0 },
		];
		const add = Math.min(calculateAnswerScore(q76, choices76), 1);
		totalInfrastructure += add;
		totalInfrastructureMax += 1;
		totalStrategie += add;
		totalStrategieMax += 1;
	}

	// --- QUESTION 77 (Infrastructure + Stratégie, dépend de Q76) ---
	const q77 = responses?.["q132_votreEntreprise132[]"];
	const canAnswer77 = responses?.q131_votreEntreprise131 === "Oui";
	if (canAnswer77 && hasAnswer(q77)) {
		const choices77 = [
			{ "Oui, on a une application mobile spécifique (iOS, Android, ...)": 1 },
			{ "Oui, l'affichage du site s'adapte automatiquement aux écrans des tablettes et smartphones (Responsive design)": 0.5 },
			{ "Aucune adaptation pour l'instant": 0 },
			{ "Ne sait pas": 0 },
		];
		const raw77 = calculateAnswerScore(q77, choices77);
		const add = Math.min(raw77, 1);
		totalInfrastructure += add;
		totalInfrastructureMax += 1;
		totalStrategie += add;
		totalStrategieMax += 1;
	}

	// --- QUESTION 79 (Stratégie, dépend de Taille > 0 et Q76 = Oui) ---
	const q79 = responses?.q134_79Votre;
	const canAnswer79 = scoreTaille > 0 && responses?.q131_votreEntreprise131 === "Oui";
	if (canAnswer79 && hasAnswer(q79)) {
		const choices79 = [
			{ "Oui": 1 },
			{ "Non": 0 },
			{ "Ne sait pas": 0 },
		];
		const addStrat = calculateAnswerScore(q79, choices79);
		totalStrategie += addStrat;
		totalStrategieMax += 1;
	}

	// --- QUESTION 80 (Matrix : dépend de Taille > 0 et Q76 = Oui) ---
	if (scoreTaille > 0 && responses?.q131_votreEntreprise131 === "Oui") {
		const q80 = responses || {};
		const scoreOui = val => (val?.trim().toLowerCase() === "oui" ? 1 : 0);

		// A — Chatbot (Stratégie)
		const q80_A = q80["q135_quellesSont135[0]"];
		if (hasAnswer(q80_A)) {
			const add = scoreOui(q80_A);
			totalStrategie += add;
			totalStrategieMax += 1;
		}

		// B — Personnalisation (Stratégie)
		const q80_B = q80["q135_quellesSont135[1]"];
		if (hasAnswer(q80_B)) {
			const add = scoreOui(q80_B);
			totalStrategie += add;
			totalStrategieMax += 1;
		}

		// C — Recommandations produits (Stratégie)
		const q80_C = q80["q135_quellesSont135[2]"];
		if (hasAnswer(q80_C)) {
			const add = scoreOui(q80_C);
			totalStrategie += add;
			totalStrategieMax += 1;
		}

		// D — Commande en ligne (Stratégie + Processus)
		const q80_D = q80["q135_quellesSont135[3]"];
		if (hasAnswer(q80_D)) {
			const add = scoreOui(q80_D);
			totalStrategie += add;
			totalStrategieMax += 1;
			totalProcessus += add;
			totalProcessusMax += 1;
		}

		// H — Recrutement (Humain + Stratégie)
		const q80_H = q80["q135_quellesSont135[7]"];
		if (hasAnswer(q80_H)) {
			const add = scoreOui(q80_H);
			totalHumain += add;
			totalHumainMax += 1;
			totalStrategie += add;
			totalStrategieMax += 1;
		}

		// I — Collecte d'avis clients (Stratégie)
		const q80_I = q80["q135_quellesSont135[8]"];
		if (hasAnswer(q80_I)) {
			const add = scoreOui(q80_I);
			totalStrategie += add;
			totalStrategieMax += 1;
		}
	}

	// --- QUESTION 81 (Infrastructure + Stratégie, dépend de Q76 = Oui et Taille > 0) ---
	const q81 = responses?.["q136_votreSite136[]"];
	const canAnswer81 = scoreTaille > 0 && responses?.q131_votreEntreprise131 === "Oui";
	if (canAnswer81 && hasAnswer(q81)) {
		const choices81 = [
			{ "Perceptible (équivalents textuels pour les images, contraste, ...)": 1 },
			{ "Utilisable (navigable au clavier, limite de flashs ...)": 1 },
			{ "Compréhensible (prévisible, cohérent, assistance à la saisie, ...)": 1 },
			{ "Compatible avec les logiciels d'assistance": 1 },
			{ "Dispose d'une déclaration d'accessibilité": 1 },
			{ "Aucun de ces principes": 0 },
			{ "Ne sait pas": 0 },
		];
		const raw81 = calculateAnswerScore(q81, choices81);
		const add = Math.min(raw81, 1);
		totalInfrastructure += add;
		totalInfrastructureMax += 1;
		totalStrategie += add;
		totalStrategieMax += 1;
	}

	// --- QUESTION 82 (Stratégie, dépend de Q76 = Oui et Taille > 0) ---
	const q82 = responses?.["q137_82Par[]"];
	const canAnswer82 = scoreTaille > 0 && responses?.q131_votreEntreprise131 === "Oui";
	if (canAnswer82 && hasAnswer(q82)) {
		const choices82 = [
			{ "Vérification et amélioration du référencement naturel": 1 },
			{ "Tag ou nomination explicite des photos et images": 1 },
			{ "Suivi du nombre de visiteurs et de pages vues": 1 },
			{ "Campagnes de publicité en ligne payantes (AdWords, Boosts Facebook, etc.)": 1 },
			{ "Suivi du nombre de références (site web, réseaux sociaux, etc.) mentionnant votre entreprise": 1 },
			{ "Mesure du taux de conversion des visiteurs en acheteurs": 1 },
			{ "Analyse de la provenance du trafic (moteurs de recherche, réseaux sociaux, annonces papier, etc.)": 1 },
			{ "Aucune analyse particulière": 0 },
			{ "Autre": 0 },
		];
		const raw82 = calculateAnswerScore(q82, choices82);
		const addStrat = Math.min(raw82, 2);
		totalStrategie += addStrat;
		totalStrategieMax += 2;
	}

	// --- QUESTION 83 (Processus + Stratégie) ---
	const q83 = responses?.q138_83Votre;
	if (hasAnswer(q83)) {
		const choices83 = [
			{ "Oui": 2 },
			{ "Non": 0 },
			{ "Ne sait pas": 0 },
		];
		const raw83 = calculateAnswerScore(q83, choices83);
		const add = Math.min(raw83, 2);
		totalProcessus += add;
		totalProcessusMax += 2;
		totalStrategie += add;
		totalStrategieMax += 2;
	}

	// --- QUESTION 84 (Processus + Stratégie) ---
	const q84 = responses?.q139_votreEntreprise139;
	if (hasAnswer(q84)) {
		const choices84 = [
			{ "Oui": 1 },
			{ "Non": 0 },
			{ "Ne sait pas": 0 },
		];
		const add = Math.min(calculateAnswerScore(q84, choices84), 1);
		totalProcessus += add;
		totalProcessusMax += 1;
		totalStrategie += add;
		totalStrategieMax += 1;
	}

	// --- QUESTION 85 (Stratégie) ---
	const q85 = responses?.["q140_85Sur[]"];
	const q85_other = responses?.["q140_85Sur[other]"];
	if (hasAnswer(q85, q85_other)) {
		const choices85 = [
			{ "Facebook": 1 },
			{ "Instagram": 1 },
			{ "TikTok": 1 },
			{ "Linkedin": 1 },
			{ "X (Twitter)": 1 },
			{ "YouTube": 1 },
			{ "Ne sait pas": 0 },
			{ "Autre": 0.5 },
			{ "Aucun": 0 },
		];
		const raw85 = calculateAnswerScore(q85, choices85);
		const addStrat = Math.min(raw85, 2);
		totalStrategie += addStrat;
		totalStrategieMax += 2;
	}

	// --- QUESTION 86 (Processus + Stratégie) ---
	const q86 = responses?.q141_votreEntreprise141;
	if (hasAnswer(q86)) {
		const choices86 = [
			{ "Quotidiennement": 1 },
			{ "Une fois par semaine": 0.8 },
			{ "Une fois par mois": 0 },
			{ "Rarement": 0 },
			{ "Jamais": 0 },
			{ "Ne sait pas": 0 },
		];
		const raw86 = calculateAnswerScore(q86, choices86);
		const add = Math.min(raw86, 1);
		totalProcessus += add;
		totalProcessusMax += 1;
		totalStrategie += add;
		totalStrategieMax += 1;
	}

	// --- QUESTION 87 (Infrastructure + Processus + Stratégie) ---
	const q87 = responses?.q144_votreEntreprise144;
	if (hasAnswer(q87)) {
		const choices87 = [
			{ "Oui": 1 },
			{ "Non": 0 },
			{ "Ne sait pas": 0 },
		];
		const add = Math.min(calculateAnswerScore(q87, choices87), 1);
		totalInfrastructure += add;
		totalInfrastructureMax += 1;
		totalProcessus += add;
		totalProcessusMax += 1;
		totalStrategie += add;
		totalStrategieMax += 1;
	}

	// --- QUESTION 88 (Stratégie) ---
	const q88 = responses?.q145_88Quelle;
	if (hasAnswer(q88)) {
		const choices88 = [
			{ "Moins de 10%": 0 },
			{ "11 à 50%": 1 },
			{ "Plus de 50%": 1 },
		];
		const addStrat = Math.min(calculateAnswerScore(q88, choices88), 1);
		totalStrategie += addStrat;
		totalStrategieMax += 1;
	}

	// --- QUESTION 89 (Infrastructure + Processus, dépend de Q87) ---
	const q87_for89 = responses?.q144_votreEntreprise144;
	const q89 = responses?.["q149_quelsTypes149[]"];
	const q89_other = responses?.["q149_quelsTypes149[other]"];
	if (q87_for89 === "Oui" && hasAnswer(q89, q89_other)) {
		const choices89 = [
			{ "E-mail valant bon de commande": 0 },
			{ "Commandes en EDI, donc totalement automatisées": 2 },
			{ "E-mail signalant l'arrivée d'une commande EDI": 1 },
			{ "Commandes provenant d'un portail e-commerce tel qu'Amazon": 1 },
			{ "Commandes provenant directement du site de vente": 1 },
			{ "Cryptomonnaies": 1 },
			{ "Réseaux sociaux": 1 },
			{ "Assistants conversationnels (ex. Alexa, Siri) ou chatbots (ex. Hopla de Carrefour)": 1 },
			{ "Applications mobiles (ex. Payconiq, PayPal)": 1 },
			{ "Services sur abonnement": 1 },
			{ "Autre": 0 },
		];
		const raw89 = calculateAnswerScore(q89, choices89);
		const add = Math.min(raw89, 2);
		totalInfrastructure += add;
		totalInfrastructureMax += 2;
		totalProcessus += add;
		totalProcessusMax += 2;
	}

	// --- QUESTION 90 (Processus, dépend de Q87) ---
	const q87_for90 = responses?.q144_votreEntreprise144;
	const q90 = responses?.q150_90Devezvous;
	if (q87_for90 === "Oui" && hasAnswer(q90)) {
		const choices90 = [
			{ "Oui": 1 },
			{ "Non": 0 },
			{ "Ne sait pas": 0 },
		];
		const addProc = Math.min(calculateAnswerScore(q90, choices90), 1);
		totalProcessus += addProc;
		totalProcessusMax += 1;
	}

	// --- QUESTION 91 (Processus + Stratégie) ---
	const q91 = responses?.q153_votreEntreprise153;
	if (hasAnswer(q91)) {
		const choices91 = [
			{ "Oui": 1 },
			{ "Non": 0 },
			{ "Ne sait pas": 0 },
		];
		const add = Math.min(calculateAnswerScore(q91, choices91), 1);
		totalProcessus += add;
		totalProcessusMax += 1;
		totalStrategie += add;
		totalStrategieMax += 1;
	}

	// --- QUESTION 92 (Infrastructure + Processus, dépend de Q91) ---
	const q91_for92 = responses?.q153_votreEntreprise153;
	const q92 = responses?.["q155_quelsTypes155[]"];
	const q92_other = responses?.["q155_quelsTypes155[other]"];
	if (q91_for92 === "Oui" && hasAnswer(q92, q92_other)) {
		const choices92 = [
			{ "EDI vrai (machine to machine)": 1 },
			{ "Web EDI ou EDI light (transcodé en mail ou via un autre média) VPN (réseau privé virtuel sur Internet)": 0.8 },
			{ "EXTRANET (site web accessible avec authentification fournissant des services spécifiques aux clients, partenaires, etc.)": 0.8 },
			{ "API (Interface d'échange de données entre ordinateurs)": 1 },
			{ "Ne sait pas": 0 },
			{ "Autre à préciser": 0 },
		];
		const raw92 = calculateAnswerScore(q92, choices92);
		const add = Math.min(raw92, 2);
		totalInfrastructure += add;
		totalInfrastructureMax += 2;
		totalProcessus += add;
		totalProcessusMax += 2;
	}

	const totalGlobal = Number(
    (
      (totalInfrastructure + totalHumain + totalProcessus + totalStrategie) / 4
    ).toFixed(1)
  );

  return {
    scoreTaille,
    totalInfrastructure,
    totalInfrastructureMax,
    totalHumain,
    totalHumainMax,
    totalProcessus,
    totalProcessusMax,
    totalStrategie,
    totalStrategieMax,
		totalGlobal,
  };
}